import { useCallback, useEffect, useMemo, useState } from 'react';
import { TriggeredBy } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { motion } from 'motion/react';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { ThreadBodyHooks } from '@frontend/thread-body';
import { theme } from '@frontend/theme';
import { Dot, NakedButton, SwitchField, Text, useAlert, useFormField } from '@frontend/design-system';
import { aiAssistantStyles } from '../components/header-components/ai-assistant-button';
import { buttonStyles } from './thread-options-drawer';

type Props = {
  groupId: string;
  threadId: string;
  locationPhone: string;
  /** True when thread is calculated but doesn't exist yet (new conversation) */
  isNew?: boolean;
};

export const AiDrawerItem = ({ groupId, threadId, locationPhone, isNew }: Props) => {
  const { t } = useTranslation('mini-chat');
  const alert = useAlert();
  const [botIsActive, setBotIsActive] = useState<boolean>(false);

  const {
    data: { allLocationPhones },
  } = InboxLocationPhones.Hooks.useLocationPhones();

  const { getThreadQuery } = ThreadBodyHooks.useGetThreadData({
    threadId: threadId,
    locationId: groupId,
    isNew,
  });

  const thread = getThreadQuery?.data?.pages?.[0]?.thread as Thread;
  const isActive = !!thread?.aiStatus?.isActive;

  const hasDefaultAiNumber = useMemo<boolean>(() => {
    return (
      allLocationPhones?.some(
        (locationPhoneObj) => locationPhoneObj.locationPhone === locationPhone && locationPhoneObj.defaultAi
      ) ?? false
    );
  }, [locationPhone, allLocationPhones]);

  const aiBotToggle = useFormField(
    {
      type: 'switch',
      value: botIsActive,
    },
    [botIsActive]
  );

  const setBotActiveMutation = SMSDataV4.Mutations.useSetBotActiveMutation({
    options: {
      onSuccess: () => {
        alert.success(
          t('AI Receptionist is now active in this thread and will continue monitoring this conversation.')
        );
      },
    },
  });

  const setBotInactiveMutation = SMSDataV4.Mutations.useSetBotInactiveMutation({
    options: {
      onSuccess: () => {
        alert.success(
          t('AI Receptionist is no longer active in this thread. It will return automatically in 30 days.')
        );
      },
    },
  });

  const handleToggle = useCallback(() => {
    if (botIsActive) {
      setBotInactiveMutation.mutate({
        locationId: groupId,
        threadId,
        triggeredBy: TriggeredBy.TRIGGERED_BY_WEAVE_USER,
      });
    } else {
      setBotActiveMutation.mutate({
        locationId: groupId,
        threadId,
        triggeredBy: TriggeredBy.TRIGGERED_BY_WEAVE_USER,
      });
    }
    setBotIsActive(!botIsActive);
  }, [botIsActive, groupId, threadId, setBotActiveMutation, setBotInactiveMutation]);

  useEffect(() => {
    setBotIsActive(isActive);
  }, [isActive]);

  if (!hasDefaultAiNumber) return null;

  return (
    <NakedButton
      css={buttonStyles}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      trackingId='mini-chat-ai-toggle'
    >
      <span
        css={{
          display: 'flex',
          gap: theme.spacing(1),
          alignItems: 'center',
        }}
      >
        <motion.div
          css={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Icon css={aiAssistantStyles} name='aiweave-assistant' size={16} />
          <Dot
            color={botIsActive ? 'success' : 'neutral'}
            css={{
              position: 'static',
              display: 'inline-flex',
              transform: `translateY(0.2em) translateX(-0.2em)`,
            }}
          />
        </motion.div>
        <Text>{t('Ai Receptionist')}</Text>
      </span>
      <span
        css={{
          display: 'flex',
          gap: theme.spacing(1),
          alignItems: 'center',
        }}
      >
        <SwitchField {...aiBotToggle} name='ai-bot-toggle' label='' onChange={handleToggle} />
      </span>
    </NakedButton>
  );
};
